import { useId } from 'react'
import { useTranslation } from 'react-i18next'

/** A conceptual topology: ingestion, storage, parallel processing and delivery. */
export function TelemetryDiagram() {
  const { t } = useTranslation('lab')
  const id = useId().replace(/:/g, '')
  const label = (key: string) => t(`journal.${key}`)
  const node = (
    key: string,
    detail: string,
    x: number,
    y: number,
    width = 150,
    accent = false,
  ) => (
    <g
      className={`architecture-node${accent ? ' architecture-node--hub' : ''}`}
    >
      <rect x={x} y={y} width={width} height={80} rx={6} />
      <text className="architecture-node-title" x={x + 16} y={y + 32}>
        {label(key)}
      </text>
      <text className="architecture-node-detail" x={x + 16} y={y + 56}>
        {label(detail)}
      </text>
    </g>
  )
  const defs = (suffix: string) => (
    <defs>
      <marker
        id={`${id}-${suffix}`}
        viewBox="0 0 8 8"
        refX="7"
        refY="4"
        markerWidth="6"
        markerHeight="6"
        orient="auto-start-reverse"
      >
        <path
          d="M1 1 L7 4 L1 7"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.3"
        />
      </marker>
    </defs>
  )
  const wire = (d: string, suffix: string) => (
    <path
      className="architecture-wire"
      d={d}
      markerEnd={`url(#${id}-${suffix})`}
    />
  )
  return (
    <figure className="architecture-figure">
      <div className="architecture-canvas">
        <svg
          className="architecture-desktop"
          viewBox="0 0 1040 490"
          role="img"
          aria-labelledby={`${id}-title ${id}-desc`}
        >
          <title id={`${id}-title`}>{label('architectureTitle')}</title>
          <desc id={`${id}-desc`}>{label('architectureDescription')}</desc>
          {defs('wide')}
          <rect
            className="architecture-boundary"
            x="196"
            y="56"
            width="824"
            height="414"
            rx="10"
          />
          <text className="architecture-zone" x="26" y="34">
            {label('edgeZone')}
          </text>
          <text className="architecture-zone" x="218" y="34">
            {label('platformZone')}
          </text>
          <path className="architecture-zone-rule" d="M26 56H158" />
          <text className="architecture-column" x="222" y="94">
            {label('ingest')}
          </text>
          <text className="architecture-column" x="428" y="94">
            {label('store')}
          </text>
          <text className="architecture-column" x="636" y="94">
            {label('process')}
          </text>
          <text className="architecture-column" x="844" y="94">
            {label('deliver')}
          </text>
          <g className="architecture-devices">
            <rect x="38" y="152" width="88" height="52" rx="5" />
            <rect x="28" y="164" width="88" height="52" rx="5" />
            <rect x="18" y="176" width="88" height="52" rx="5" />
            <path d="M34 194h20m-20 9h34" />
            <circle cx="90" cy="201" r="3" />
          </g>
          <text className="architecture-node-title" x="20" y="263">
            {label('device')}
          </text>
          <text className="architecture-node-detail" x="20" y="286">
            {label('deviceDetail')}
          </text>
          <text
            className="architecture-wire-label"
            x="161"
            y="190"
            textAnchor="middle"
          >
            {label('telemetry')}
          </text>
          {wire('M108 212 H222', 'wide')}
          {node('hub', 'hubDetail', 222, 172, 150, true)}
          {wire('M372 212 H428', 'wide')}
          {node('bigData', 'dataDetail', 428, 172)}
          <path className="architecture-wire" d="M578 212 H606 M606 158 V322" />
          <circle className="architecture-junction" cx="606" cy="212" r="3" />
          {wire('M606 158 H636', 'wide')}
          {wire('M606 322 H636', 'wide')}
          {node('analytics', 'analyticsDetail', 636, 118)}
          {node('ai', 'aiDetail', 636, 282)}
          {wire('M786 158 H844', 'wide')}
          {wire('M786 322 H844', 'wide')}
          {node('app', 'appDetail', 844, 118)}
          {node('dashboard', 'dashboardDetail', 844, 282)}
          <path className="architecture-access-rule" d="M218 397 H998" />
          <path
            className="architecture-lock"
            d="M229 429v-6a6 6 0 0 1 12 0v6m-14 0h16v13h-16z"
          />
          <text className="architecture-access-title" x="256" y="437">
            RBAC
          </text>
          <text className="architecture-node-detail" x="316" y="437">
            {label('rbac')}
          </text>
          <text className="architecture-scope" x="995" y="437" textAnchor="end">
            {label('systemWide')}
          </text>
        </svg>
        <svg
          className="architecture-mobile"
          viewBox="0 0 380 720"
          role="img"
          aria-labelledby={`${id}-mobile-title ${id}-mobile-desc`}
        >
          <title id={`${id}-mobile-title`}>{label('architectureTitle')}</title>
          <desc id={`${id}-mobile-desc`}>
            {label('architectureDescription')}
          </desc>
          {defs('small')}
          <rect
            className="architecture-boundary"
            x="4"
            y="178"
            width="372"
            height="530"
            rx="10"
          />
          <text className="architecture-zone" x="20" y="28">
            {label('edgeZone')}
          </text>
          {node('device', 'deviceDetail', 20, 45, 340)}
          {wire('M190 125 V195', 'small')}
          <text className="architecture-wire-label" x="207" y="158">
            {label('telemetry')}
          </text>

          {node('hub', 'hubDetail', 20, 195, 340, true)}
          {wire('M190 275 V305', 'small')}
          {node('bigData', 'dataDetail', 20, 305, 340)}
          <path
            className="architecture-wire"
            d="M190 385 V409 H100 M190 409 H280"
          />
          {wire('M100 409 V437', 'small')}
          {wire('M280 409 V437', 'small')}
          <circle className="architecture-junction" cx="190" cy="409" r="3" />
          {node('analytics', 'analyticsDetail', 20, 437, 160)}
          {node('ai', 'aiDetail', 200, 437, 160)}
          {wire('M100 517 V550', 'small')}
          {wire('M280 517 V550', 'small')}
          {node('app', 'appDetail', 20, 550, 160)}
          {node('dashboard', 'dashboardDetail', 200, 550, 160)}
          <path className="architecture-access-rule" d="M20 650 H360" />
          <text className="architecture-access-title" x="20" y="680">
            RBAC
          </text>
          <text className="architecture-node-detail" x="76" y="680">
            {label('rbac')}
          </text>
        </svg>
      </div>
      <figcaption>
        <span>{label('architectureTitle')}</span>
        <span>{label('conceptual')}</span>
      </figcaption>
    </figure>
  )
}
